import { combineReducers, Reducer } from 'redux';

import { EMPTY_OBJECT } from 'uf/base';
import { typeAssertNever } from 'uf/base/never';
import {
  FailureAction,
  LoadAction,
  LoadActionTypes,
  SuccessAction,
} from 'uf/data/ActionTypes';
import {
  DataStateMap,
  makeErrorState,
  makeLoadedState,
  makeLoadingState,
} from 'uf/data/dataState';
import { makeKeyedPromiseReducer } from 'uf/data/makeKeyedPromiseReducer';
import { makeWebsocketActionType } from 'uf/websockets/actionHelpers';

import { TaskMessage } from './MessageTypes';
import { NotificationTypes } from './NotificationTypes';
import { BindTaskMap, CombinedTaskState } from './state';
import { TaskStatuses } from './TaskStatuses';
import { TaskTypes } from './TaskTypes';

/**
 * Creates a reducer that will keep track of outstanding tasks by a certain key
 *
 * Paired with bindTaskToResource, this allows a mapping from resourceId to an
 * array of tasks that have / are / will run for that resource.
 *
 */
export function makeBindTaskReducer(actionType: string): Reducer<BindTaskMap> {
  return (state: BindTaskMap = {}, action): BindTaskMap => {
    if (action.type !== actionType) {
      return state;
    }

    const tasks = state[action.resourceId] || [];
    const newState = {
      ...state,
      [action.resourceId]: [...tasks, action.taskId],
    };
    return newState;
  };
}

/**
 * Create a reducer which tracks the relationship between an HTTP call that
 * enqueues a task, and the completion of that task. This version is for modern
 * tasks that use a single task notification key, but use the `status` of the
 * websocket message to track the enqueued/running/done states.
 *
 * Note that this is meant to be used with `bindTaskToResource` inside an epic.
 *
 * @param enqueueActionTypes The LoadActions (LOAD/SUCCESS/FAILURE) for the HTTP
 *        call that starts the task. Usually these are the auto-generated ones
 *        in `uf-api`.
 * @param bindTaskActionType The action type that the epic will listen for.
 * @param taskActionType The async action type.
 */
export function makeCombinedTaskReducer<D, M, E = any>(
  enqueueActionTypes: LoadActionTypes,
  bindTaskActionType: string,
  taskActionType: TaskTypes,
): Reducer<CombinedTaskState<D, M, E>> {
  return combineReducers<CombinedTaskState<D, M, E>>({
    enqueued: makeKeyedPromiseReducer(enqueueActionTypes),
    taskIds: makeBindTaskReducer(bindTaskActionType),
    tasks: makeTaskReducer<M>(taskActionType),
  });
}

/**
 * Create a reducer which tracks the relationship between an HTTP call that
 * enqueues a task, and the completion of that task. This version is for legacy
 * tasks that have 3 separate task notifications.
 *
 * Note that this is meant to be used with `bindTaskToResource` inside an epic.
 *
 * @param enqueueActionTypes The LoadActions (LOAD/SUCCESS/FAILURE) for the HTTP
 *        call that starts the task. Usually these are the auto-generated ones
 *        in `uf-api`.
 * @param bindTaskActionType The action type that the epic will listen for.
 * @param taskActionTypes The async action types, generally created with
 *        `makeWebsocketActionTypes()`, which maps LOAD/SUCCESS/FAILURE to
 *        specific websocket messages.
 */
export function makeCombinedLegacyTaskReducer<D, M, E = any>(
  enqueueActionTypes: LoadActionTypes,
  bindTaskActionType: string,
  taskActionTypes: LoadActionTypes,
): Reducer<CombinedTaskState<D, M, E>> {
  return combineReducers<CombinedTaskState<D, M, E>>({
    enqueued: makeKeyedPromiseReducer(enqueueActionTypes),
    taskIds: makeBindTaskReducer(bindTaskActionType),
    // TODO: don't use makeKeyedPromiseReducer here so we can handle all of the different
    // TaskStatuses appropriately. (running, cancelled)
    tasks: makeKeyedPromiseReducer(taskActionTypes),
  });
}

interface TaskAction {
  type: NotificationTypes.TASK_NOTIFICATION;
  result: TaskMessage;
  key: string;
}

function makeTaskReducer<T>(taskType: TaskTypes): Reducer<DataStateMap<T>> {
  return function reducer(
    state = EMPTY_OBJECT,
    action: TaskAction,
  ): DataStateMap<T> {
    if (
      action.type !==
        makeWebsocketActionType(NotificationTypes.TASK_NOTIFICATION) ||
      action.result.task_type !== taskType
    ) {
      return state;
    }

    const { key, result } = action;
    switch (result.status) {
      case TaskStatuses.RUNNING:
      case TaskStatuses.ENQUEUED:
        return {
          ...state,
          [key]: makeLoadingState(state[key], {} as LoadAction),
        };

      case TaskStatuses.DONE:
        return {
          ...state,
          [key]: makeLoadedState(state[key], {
            result: result.result,
          } as SuccessAction<T>),
        };
      case TaskStatuses.CANCELLED:
      case TaskStatuses.ERROR:
        return {
          ...state,
          [key]: makeErrorState(state[key], {
            error: result.problem,
          } as FailureAction),
        };

      default:
        typeAssertNever(result.status);
        return state;
    }
  };
}
